import { Button, Col, Row, Table, Tooltip, Input, Select, Space, Spin } from "antd";
import { CopyOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import "./templates.css";
import { IProductTemplate } from "@drone-parts.garphild.pro/interfaces";
import { Suspense, useEffect, useState } from "react";
import templatesStorage from "../../../storages/templatesStorage";
import { observer } from "mobx-react-lite";
import Toolbar from "../../../v2/shared-components/Toolbar";
import TemplateEditForm from "../components/TemplateEditForm";
import EditTemplateDialog from "../../../dialogs/edit-template-dialog";

function Templates() {
  const { t } = useTranslation('templates', { useSuspense: false });
  const [isNewDialogVisible, setIsNewDialogVisible] = useState(false);
  const [isTemplateSelected, setIsTemplateSelected] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  
  // Get unique template types for the filter
  const getUniqueTypes = () => {
    const types = templatesStorage.templates
      .map(template => template.type)
      .filter((type, index, self) => 
        type && self.indexOf(type) === index
      );
    return types;
  };
  const {
    editTemplate,
    createTemplate,
    cloneTemplateForEdit,
  } = templatesStorage;

  useEffect(() => {
    templatesStorage.loadTemplates();
  }, []);

  const columns = [
    {
      title: t('column.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('column.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('column.type'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('column.total'),
      dataIndex: 'total',
      key: 'total',
    },
    {
      title: t('column.actions'),
      dataIndex: 'actions',
      key: 'actions',
      render: (text: string, record: IProductTemplate) => (
        <Tooltip title={t('action.clone')}>
          <Button
            icon={<CopyOutlined />}
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              cloneTemplateForEdit(record._id);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const showCreateDialog = () => {
    createTemplate();
    setIsNewDialogVisible(true);
  };

  return (
    <div className="templates-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>{t('title')}</h1>
        <Toolbar>
          <Button 
            type="primary"
            onClick={showCreateDialog}
          >
            {t('button.addNew')}
          </Button>
        </Toolbar>
      </div>
      <Suspense fallback={<Spin />}>
        <Row gutter={24} style={{ marginBottom: '20px', width: '100%' }}>
          <Col span={12}>
            <div style={{ marginBottom: '16px' }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input 
                  placeholder={t('search.placeholder')} 
                  prefix={<SearchOutlined />} 
                  allowClear
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: '70%' }}
                />
                <Select
                  placeholder={t('filter.byType')}
                  allowClear
                  style={{ width: '30%' }}
                  onChange={(value) => setTypeFilter(value)}
                >
                  {getUniqueTypes().map(type => (
                    <Select.Option key={type} value={type}>{type}</Select.Option>
                  ))}
                </Select>
              </Space.Compact>
            </div>
            <div className="templates-table-container" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
              <Table
                size="small"
                columns={columns}
                dataSource={templatesStorage.templates?.filter(template => {
                  const query = searchQuery.toLowerCase();
                  const matchesSearch = !query || (
                    template.title?.toLowerCase().includes(query) ||
                    template.description?.toLowerCase().includes(query)
                  );
                  const matchesType = !typeFilter || template.type === typeFilter;
                  
                  return matchesSearch && matchesType;
                }) || []}
                rowKey="_id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total, range) => t('pagination.total', { from: range[0], to: range[1], total })
                }}
                rowClassName={(record) => record._id === selectedTemplateId ? 'selected-row' : ''}
                onRow={(record) => ({
                  onClick: () => {
                    editTemplate(record._id);
                    setIsTemplateSelected(true);
                    setSelectedTemplateId(record._id);
                  },
                  style: { cursor: 'pointer' }
                })}
              />
            </div>
          </Col>
          
          <Col span={12}>
            <div className="template-edit-container" style={{ 
              padding: '16px', 
              border: '1px solid #f0f0f0', 
              borderRadius: '4px', 
              height: '100%',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 250px)' 
            }}>
              {isTemplateSelected ? (
                <TemplateEditForm />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px', flexDirection: 'column', color: '#999' }}>
                  <span role="img" aria-label="clipboard" style={{ fontSize: '48px', marginBottom: '16px' }}>📋</span>
                  <h2>{t('placeholder.title')}</h2>
                  <p>{t('placeholder.instruction')}</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Suspense>
      {isNewDialogVisible && (
        <EditTemplateDialog onClose={() => {
          setIsNewDialogVisible(false);
          // Restore the selected template if there was one
          if (selectedTemplateId) {
            editTemplate(selectedTemplateId);
            setIsTemplateSelected(true);
          }
        }} />
      )}
    </div>
  );
}

export default observer(Templates);
